/**
 * Display pricing for self-serve SaaS plans.
 * Stripe amounts live on Price objects; these are for UI/marketing only.
 *
 * Catalog (buyer-facing):
 *   - Food Mover Lite        $199/mo
 *   - Food Delivery Pro      $250/mo  (home delivery only)
 *   - Food Mover Pro         $500/mo
 *   - Home Delivery add-on   +$250/mo (on Lite or Pro only)
 *
 * Free nonprofit is signup, not a paid plan card.
 * Basic remains the donor paid tier.
 *
 * Internal bundle keys (admin / Stripe product mapping only — not plan cards):
 *   - foodMoverLiteHome = Food Mover Lite + Home Delivery ($449)
 *   - foodMoverProHome  = Food Mover Pro + Home Delivery ($750)
 *
 * White Label is a paid Pro add-on (not included in plan price), sales/invoice
 * only — see WHITE_LABEL_PRICING; not a self-serve catalog plan.
 */

const HOME_DELIVERY_ADDON_PRICING = {
  label: 'Home Delivery',
  shortLabel: 'Home Delivery',
  monthly: 250,
  monthlyAnnual: 250,
  monthlyMonthToMonth: 250,
  annualTotal: 3000,
  implementationFee: 0,
  selfServe: false,
  // Base plans this add-on can attach to (not Food Delivery Pro — that already is HD)
  appliesTo: ['foodMoverLite', 'foodMover'],
  note: 'Add home delivery / distributions to Food Mover Lite or Food Mover Pro for +$250/mo. Contact sales or request via support; not self-serve checkout yet.',
};

const PLAN_PRICING = {
  free: {
    label: 'Nonprofit account',
    shortLabel: 'Free',
    monthlyAnnual: 0,
    monthlyMonthToMonth: 0,
    annualTotal: 0,
    implementationFee: 0,
    // Signup tier — not shown as a plan card
    isSignupTier: true,
  },
  basic: {
    label: 'Basic',
    shortLabel: 'Basic',
    monthlyAnnual: 19,
    monthlyMonthToMonth: 19,
    annualTotal: 228,
    implementationFee: 0,
  },
  foodMoverLite: {
    label: 'Food Mover Lite',
    shortLabel: 'Lite',
    monthlyAnnual: 199,
    monthlyMonthToMonth: 199,
    annualTotal: 2388,
    implementationFee: 0,
  },
  foodDeliveryPro: {
    label: 'Food Delivery Pro',
    shortLabel: 'Delivery Pro',
    monthlyAnnual: 250,
    monthlyMonthToMonth: 250,
    annualTotal: 3000,
    implementationFee: 0,
  },
  // Internal: Lite + Home Delivery add-on (not a catalog plan card)
  foodMoverLiteHome: {
    label: 'Food Mover Lite + Home Delivery',
    shortLabel: 'Lite + Home Delivery',
    monthlyAnnual: 199 + HOME_DELIVERY_ADDON_PRICING.monthly,
    monthlyMonthToMonth: 199 + HOME_DELIVERY_ADDON_PRICING.monthly,
    annualTotal: (199 + HOME_DELIVERY_ADDON_PRICING.monthly) * 12,
    implementationFee: 0,
    isHomeDeliveryBundle: true,
    basePlan: 'foodMoverLite',
  },
  foodMover: {
    label: 'Food Mover Pro',
    shortLabel: 'Pro',
    monthlyAnnual: 500,
    monthlyMonthToMonth: 500,
    annualTotal: 6000,
    implementationFee: 0,
  },
  // Internal: Pro + Home Delivery add-on (not a catalog plan card)
  foodMoverProHome: {
    label: 'Food Mover Pro + Home Delivery',
    shortLabel: 'Pro + Home Delivery',
    monthlyAnnual: 500 + HOME_DELIVERY_ADDON_PRICING.monthly,
    monthlyMonthToMonth: 500 + HOME_DELIVERY_ADDON_PRICING.monthly,
    annualTotal: (500 + HOME_DELIVERY_ADDON_PRICING.monthly) * 12,
    implementationFee: 0,
    isHomeDeliveryBundle: true,
    basePlan: 'foodMover',
  },
  freshcue: {
    label: 'Freshcue',
    shortLabel: 'Freshcue',
    monthlyAnnual: 250,
    monthlyMonthToMonth: 250,
    annualTotal: 3000,
    implementationFee: 0,
  },
};

const WHITE_LABEL_PRICING = {
  label: 'White Label',
  annualFlatAllHubs: 3000,
  setupFee: 10000,
  billing: 'annual_only',
  selfServe: false,
  // Not a standalone plan — billed on top of a Pro subscription
  note: 'Add-on only: not included in any plan. Requires Food Mover Pro (with or without Home Delivery), then +$3,000/yr flat (all hubs) and $10,000 setup, annual invoice only. Contact sales.',
};

const getPlanPricing = (planKey) => PLAN_PRICING[planKey] || null;

const formatMoney = (amount) => {
  if (amount == null) return '—';
  if (amount === 0) return 'Free';
  return `$${Number(amount).toLocaleString('en-US')}`;
};

const formatPlanPriceSummary = (planKey) => {
  const p = getPlanPricing(planKey);
  if (!p) return '';
  if (p.monthlyAnnual === 0 && p.monthlyMonthToMonth === 0) return 'Free';
  if (p.monthlyAnnual === p.monthlyMonthToMonth) {
    return `${formatMoney(p.monthlyMonthToMonth)}/mo`;
  }
  const annual = `${formatMoney(p.monthlyAnnual)}/mo · ${formatMoney(p.annualTotal)}/yr`;
  const mtm = `${formatMoney(p.monthlyMonthToMonth)}/mo`;
  return `Annual ${annual}; month-to-month ${mtm}`;
};

module.exports = {
  PLAN_PRICING,
  HOME_DELIVERY_ADDON_PRICING,
  WHITE_LABEL_PRICING,
  getPlanPricing,
  formatMoney,
  formatPlanPriceSummary,
};
